import React from 'react';
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, PlayCircle, Brain, Star, MessageCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/shared/SEO';

const PracticeLanding: React.FC = () => {
  const navigate = useNavigate();

  const handleStartPractice = () => {
    navigate('/practice-start');
  };

  // Create practice page structured data
  const practicePageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "AI Interview Practice - RoundOne",
    "description": "Practice interviews anytime with our AI-powered interviewer. Build confidence and improve your interview skills without registration.",
    "url": "https://roundone.ai/practice"
  };

  // Create HowTo structured data for the interview practice process
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Practice Interviews with AI",
    "description": "Step-by-step guide to practice job interviews with RoundOne AI's interview simulator",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Select Your Role",
        "text": "Choose the job position you're preparing for and your experience level."
      },
      {
        "@type": "HowToStep",
        "name": "Choose Skills",
        "text": "Select the specific skills you want to focus on during your practice."
      },
      {
        "@type": "HowToStep",
        "name": "Start Practicing",
        "text": "Begin your interview session with our AI interviewer using your microphone."
      },
      {
        "@type": "HowToStep",
        "name": "Review & Improve",
        "text": "After completion, review your interview transcript and audio recording."
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-[#f8f7ff]">
      <SEO 
        title="AI Interview Practice - Prepare With Our Virtual Interviewer"
        description="Practice job interviews for free with our AI interviewer. Get instant feedback, role-specific questions, and build confidence for your next job interview."
        url="/practice"
        schema={[practicePageSchema, howToSchema]}
      />
      
      <Header />

      {/* Hero Section */}
      <section className="flex-1 pt-28 pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <div className="max-w-3xl space-y-8">
              {/* New AI Capsule */}
              <div className="mb-6">
                <Link to="/blog/roundone-ai-2-0-a-revolutionary-leap-forward-in-ai-driven-recruitment">
                  <span className="inline-block text-xs bg-roundone-light-purple/10 text-roundone-purple px-3 py-1 rounded-full mb-4">
                    <span className="font-bold">NEW</span> <span className="ml-1">RoundOne AI 2.0 is here! →</span>
                  </span>
                </Link>
              </div>
              
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                  <span className="text-roundone-dark-purple">Master Your Interview</span>
                  <span className="text-roundone-light-purple"> Skills with AI</span>
                </h1>
                <p className="text-xl text-[#1A1F2C]/70">
                  Practice interviews anytime, anywhere with our AI-powered interviewer. 
                  Get real-time feedback and improve your confidence for your next job interview.
                </p>
              </div>
              
              <div className="flex justify-center">
                <Button 
                  onClick={handleStartPractice}
                  className="gradient-button py-6 px-8 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center"
                >
                  <PlayCircle className="mr-2" size={20} />
                  Try Practice Interview
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-6 pt-4 justify-center">
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-green-500" size={20} />
                  <span className="text-[#1A1F2C]/80">No registration required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-green-500" size={20} />
                  <span className="text-[#1A1F2C]/80">100% free to try</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-green-500" size={20} />
                  <span className="text-[#1A1F2C]/80">AI-powered feedback</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1A1F2C] mb-4">Why Practice with Our AI Interviewer?</h2>
            <p className="text-[#1A1F2C]/70 max-w-3xl mx-auto">
              Our AI-powered practice interviews help you prepare for real interviews by simulating real-world scenarios.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Benefit 1 */}
            <div className="bg-[#F8F7FF] p-8 rounded-xl border border-[#9b87f5]/20">
              <div className="w-14 h-14 bg-[#9b87f5]/20 rounded-lg flex items-center justify-center mb-6">
                <Brain className="text-[#7662DA]" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-[#1A1F2C] mb-3">AI-Powered Interviews</h3>
              <p className="text-[#1A1F2C]/70">
                Practice with our advanced AI interviewer that adapts questions based on your role and experience level.
              </p>
            </div>
            
            {/* Benefit 2 */}
            <div className="bg-[#F8F7FF] p-8 rounded-xl border border-[#9b87f5]/20">
              <div className="w-14 h-14 bg-[#9b87f5]/20 rounded-lg flex items-center justify-center mb-6">
                <Star className="text-[#7662DA]" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-[#1A1F2C] mb-3">Role-Specific Questions</h3>
              <p className="text-[#1A1F2C]/70">
                Get asked relevant questions for your target role, whether you're applying for engineering, sales, or management positions.
              </p>
            </div>
            
            {/* Benefit 3 */}
            <div className="bg-[#F8F7FF] p-8 rounded-xl border border-[#9b87f5]/20">
              <div className="w-14 h-14 bg-[#9b87f5]/20 rounded-lg flex items-center justify-center mb-6">
                <MessageCircle className="text-[#7662DA]" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-[#1A1F2C] mb-3">Real-time Conversation</h3>
              <p className="text-[#1A1F2C]/70">
                Engage in natural, flowing conversations that mimic real interview dynamics and help you practice thinking on your feet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#F8F7FF] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1A1F2C] mb-4">How It Works</h2>
            <p className="text-[#1A1F2C]/70 max-w-3xl mx-auto">
              Getting started with our practice interviews is easy and takes just a few minutes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4">
                  1
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Select Your Role</h3>
                <p className="text-[#1A1F2C]/70">
                  Choose the job position you're preparing for and your experience level.
                </p>
              </div>
              
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="relative">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4">
                  2
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Choose Skills</h3>
                <p className="text-[#1A1F2C]/70">
                  Select the specific skills you want to focus on during your practice.
                </p>
              </div>
              
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="relative">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4">
                  3
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Start Practicing</h3>
                <p className="text-[#1A1F2C]/70">
                  Begin your interview session with our AI interviewer using your microphone.
                </p>
              </div>
              
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            
            {/* Step 4 */}
            <div>
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4">
                  4
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Review & Improve</h3>
                <p className="text-[#1A1F2C]/70">
                  After completion, review your interview transcript and audio recording.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center mt-16">
            <Button 
              onClick={handleStartPractice}
              className="gradient-button py-6 px-8 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center"
            >
              <PlayCircle className="mr-2" size={20} />
              Start Practice Interview Now
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1A1F2C] mb-4">What Users Say</h2>
            <p className="text-[#1A1F2C]/70 max-w-3xl mx-auto">
              Hear from job seekers who improved their interview skills with our practice platform.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-[#F8F7FF] p-8 rounded-xl border border-[#9b87f5]/20">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="text-yellow-400 fill-yellow-400" size={18} />
                ))}
              </div>
              <p className="text-[#1A1F2C]/80 mb-6 italic">
                "After practicing with this AI interviewer, I felt so much more confident in my actual job interview. The questions were spot-on for my role, and I was much better prepared!"
              </p>
              <div>
                <p className="font-semibold text-[#1A1F2C]">Sarah K.</p>
                <p className="text-[#1A1F2C]/60 text-sm">Software Engineer</p>
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="bg-[#F8F7FF] p-8 rounded-xl border border-[#9b87f5]/20">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="text-yellow-400 fill-yellow-400" size={18} />
                ))}
              </div>
              <p className="text-[#1A1F2C]/80 mb-6 italic">
                "The realistic conversation flow really helped me get comfortable with answering tough questions. Being able to practice anytime was a game-changer for my interview prep."
              </p>
              <div>
                <p className="font-semibold text-[#1A1F2C]">Michael R.</p>
                <p className="text-[#1A1F2C]/60 text-sm">Product Manager</p>
              </div>
            </div>
            
            {/* Testimonial 3 */}
            <div className="bg-[#F8F7FF] p-8 rounded-xl border border-[#9b87f5]/20">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="text-yellow-400 fill-yellow-400" size={18} />
                ))}
              </div>
              <p className="text-[#1A1F2C]/80 mb-6 italic">
                "I used to get so nervous during interviews. This practice tool helped me build confidence and refine my answers. I just landed my dream job thanks to all the practice!"
              </p>
              <div>
                <p className="font-semibold text-[#1A1F2C]">Jessica T.</p>
                <p className="text-[#1A1F2C]/60 text-sm">Marketing Specialist</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#9b87f5]/20 to-[#7662DA]/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#1A1F2C] mb-4">Ready to Ace Your Next Interview?</h2>
          <p className="text-[#1A1F2C]/70 max-w-2xl mx-auto mb-8">
            Start practicing now with our AI interviewer and boost your confidence for your upcoming job interviews.
            No registration required - it's completely free to try!
          </p>
          
          <div className="flex justify-center mt-16">
            <Button 
              onClick={handleStartPractice}
              className="gradient-button py-6 px-8 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center"
            >
              <PlayCircle className="mr-2" size={20} />
              Try Practice Interview Now
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PracticeLanding; 